<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useFileDialog } from '@vueuse/core';

import ProfilePageNotificationsView from '@/views/profiles/ProfilePageNotificationsView.vue';
import ProfilePageSettingsView from '@/views/profiles/ProfilePageSettingsView.vue';
import AccountAvatar from '@/components/UI/avatar/AccountAvatar.vue';
import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import TimeZoneSelect from '@/components/selects/TimeZoneSelect.vue';
import SmartProfileTabs from '@/components/tabs/SmartProfileTabs.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { useUserStore } from '@/stores/useUserStore';
import { fetchProfileUpdate } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { IUserUpdate } from '@/models/main';

const authStore = useAuthStore();
const userStore = useUserStore();
const route = useRoute();
const { open, onChange } = useFileDialog();

const loading = ref(false);
const user = computed(() => userStore.user);
const userTimeZoneCity = computed(() => userStore.user.timezone.split('/')[1]);
const fileAvatar = ref<File | null>(null);
const avatarUrl = ref<string | null>(null);
const fieldValue = ref('');

// Time zone lookup value updating
// ! Remove this watcher when the loading of the main layout is fixed and the profile data is in the state before the page rendering begins
watch(
  () => userStore.user.timezone,
  (data) => {
    let storeValue = userStore.user.timezone.split('/')[1];
    storeValue = storeValue === 'Lord_Howe' ? 'Lord Howe Island' : storeValue;
    fieldValue.value = storeValue;
  },
  { immediate: true },
);

const openFileDialog = (): void => {
  open({
    multiple: false,
    accept: 'image/*',
  });
};

const clearAvatarSelection = (): void => {
  if (avatarUrl.value) URL.revokeObjectURL(avatarUrl.value);
  avatarUrl.value = null;
  fileAvatar.value = null;
};

// Called when an image is selected for download
onChange((files: FileList | null): void => {
  if (!files) return;
  if (avatarUrl.value) URL.revokeObjectURL(avatarUrl.value);
  fileAvatar.value = files[0];
  const blob = new Blob([fileAvatar.value], { type: fileAvatar.value.type });
  avatarUrl.value = URL.createObjectURL(blob);
});

const handleCancelClick = (): void => {
  fieldValue.value = userTimeZoneCity.value;
  clearAvatarSelection();
};

const handleConfirmClick = async (): Promise<void> => {
  loading.value = true;
  const params: IUserUpdate = {};

  const formData: FormData = new FormData();

  const timeZoneName =
    fieldValue.value === 'Lord Howe Island' ? 'Lord_Howe' : fieldValue.value;

  if (user.value.timezone !== `Australia/${timeZoneName}`) {
    params.timezone = `Australia/${timeZoneName}`;
  }
  formData.append('payload', JSON.stringify(params));
  if (fileAvatar.value) formData.append('avatar_file', fileAvatar.value);

  try {
    await fetchProfileUpdate(formData);
    clearAvatarSelection();
  } catch {
    // Fetch errors are notified inside the fetcher
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div data-testid="profileView">
    <PageContentWrapper class="mt-4 pt-0">
      <SmartProfileTabs />

      <v-window :model-value="route.name" :touch="false" class="pt-4">
        <v-window-item :value="routeNames.PROFILE">
          <div v-if="routeNames.PROFILE === route.name">
            <div class="flex">
              <div class="relative mt-8 w-min">
                <AccountAvatar
                  :size="120"
                  color="purple"
                  background="purple"
                  :text="user && user.username ? user.username[0] : ''"
                  :image-u-r-l="user.avatar"
                  :new-image="avatarUrl ? avatarUrl : undefined"
                />
                <div
                  class="absolute top-0 -right-2 z-100 flex size-10 cursor-pointer items-center justify-center rounded-full hover:shadow"
                  :class="$style.avatarEditBadge"
                  @click="openFileDialog"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                </div>
              </div>

              <div
                class="mt-6 ml-4 flex flex-col leading-5"
                :class="$style.avatarHint"
              >
                <span>Equilateral,</span>
                <span>from 180x180 pixels</span>
              </div>
            </div>

            <!-- User details -->
            <div class="mt-8 flex">
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">First name</h4>
                <EmDashPlaceholder :item="user.first_name" />
              </div>
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">Last name</h4>
                <EmDashPlaceholder :item="user.last_name" />
              </div>
            </div>

            <div class="mt-6 flex">
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">Status</h4>
                <p class="p-0">{{ user.is_staff ? 'Staff' : 'Customer' }}</p>
              </div>
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">Customer code</h4>
                <EmDashPlaceholder :item="authStore.authData.customerCode" />
              </div>
            </div>

            <div v-if="user.available_codes?.length" class="mt-6 flex">
              <div class="flex flex-col">
                <h4 class="m-0 p-0 font-semibold">Available customer codes</h4>
                <p class="m-0 p-0">
                  {{ user.available_codes.join(', ') }}
                </p>
              </div>
            </div>

            <div class="mt-6 flex">
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">Email</h4>
                <p class="m-0 p-0">{{ user.email ? user.email : '-' }}</p>
              </div>
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">Phone number</h4>
                <EmDashPlaceholder :item="user.phone_number" />
              </div>
            </div>

            <div class="mt-6 flex">
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">Time zone</h4>
                <div class="mt-2 w-72">
                  <TimeZoneSelect v-model="fieldValue" />
                </div>
              </div>
              <div class="flex w-88 flex-col">
                <h4 class="m-0 p-0 font-semibold">State</h4>
                <EmDashPlaceholder :item="user.state?.join(', ')" />
              </div>
            </div>
          </div>
        </v-window-item>
        <v-window-item :value="routeNames.PROFILE_NOTIFICATIONS">
          <ProfilePageNotificationsView
            v-if="routeNames.PROFILE_NOTIFICATIONS === route.name"
          />
        </v-window-item>
        <v-window-item :value="routeNames.PROFILE_SETTINGS">
          <ProfilePageSettingsView
            v-if="routeNames.PROFILE_SETTINGS === route.name"
          />
        </v-window-item>
      </v-window>

      <TableFooterBar>
        <KBtn
          variant="outlined"
          append-icon="mdi-close"
          :disabled="loading"
          @click="handleCancelClick"
        >
          Cancel
        </KBtn>
        <KBtn
          append-icon="mdi-check"
          class="ml-4"
          :loading="loading"
          @click="handleConfirmClick"
        >
          Confirm
        </KBtn>
      </TableFooterBar>
    </PageContentWrapper>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.avatarEditBadge {
  background-color: colors.$color-gray-100;

  &:hover {
    background-color: colors.$color-gray-50;
  }
}

.avatarHint {
  color: colors.$color-gray-600;
}
</style>
